import Vue from 'vue'

/**
 * Methods for DietID iframe integration with SurveyForm.
 *
 * Communication with the DietID app is via doDietIdLogin, sendUserInfo, and receiveDietidMessage.
 * On questionnaire completion, DietID sends scores to our backend API via the URL specified in
 * Api.getDietIdWebhookUrl.
 */

export default {
    data() {
        return {
            dietidIframeWindow: null,
            dietidIframeUrl: null,
            dietidCompletedQuestionCount: 0,
            dietidMaxQuestions: 15  // max 4 for Screeners section, 14 for ID section, ?? for final section
        }
    },
    computed: {},
    methods: {
        onDietidIframeLoad() {
            this.dietidIframeWindow = this.$refs.dietidIframe.iframeEl.contentWindow
        },

        // called on iframe load (see SurveyForm.vue)
        onDietidLoad() {
        },

        receiveDietidMessage(event) {
            try {
                var message = JSON.parse(event.data)
                if (message.source == 'DietID') {
                    console.log('SurveyFormDietidMixin:receivedDietidMessage  message.eventName=', message.eventName)
                    console.log('SurveyFormDietidMixin:receiveDietidMessage  message=', message)
                    if (message.eventName == 'ready') {
                        console.log('SurveyFormDietidMixin:receivedDietidMessage  sending message to dietID')
                        this.sendUserInfo()
                    } else if (message.eventName == 'workflowprogresschange') {
                        // update progress bar
                        this.dietidCompletedQuestionCount += 1
                        console.log('SurveyFormDietidMixin:receivedDietidMessage  count=', this.dietidCompletedQuestionCount.toString() + '/' + this.dietidMaxQuestions.toString())
                        const qname = 'DIETID_' + (this.dietidCompletedQuestionCount)
                        this.goToQuestionByName(qname)
                    } else if (message.eventName == 'workflowcomplete') {
                        this.dietidMaxQuestions = this.dietidCompletedQuestionCount - 1
                        this.goToQuestionByName('CRA_OUTRO_DIETID')
                    }
                }
            } catch (SyntaxError) {
                // this generates noise in console, lots of things trigger SyntaxError
                console.log('syntax error in receiveDietidMessage')
            }
        },

        async doDietIdLogin() {
            let key = process.env.VUE_APP_DIETID_SSO_KEY
            console.log('SurveyFormDietidMixin:doDietIdLogin  process.env=', process.env)
            if (!key) {
                // default to ted.wcd.ngrok.io - requires ngrok server to be running locally
                // for webhook callback to work at end of questionnaire!
                // key is defined in .env.development.local
            }
            console.log('SurveyFormDietidMixin:doDietIdLogin  DietID SSO key', key.substring(0, 10) + '...')

            let sso_url = 'https://api-staging.dietid.com/api/v1/sso'
            if (process.env.VUE_APP_API_HOST == 'https://api.sustainable-health.app') {
                sso_url = 'https://api-prod.dietid.com/api/v1/sso'
            }
            console.log('SurveyFormDietIdMixin:doDietIdLogin  DietID SSO URL', sso_url)

            const survey = this.$store.state.survey // loaded by SurveyForm

            // all optional except partnerUserId
            const data = {
                partnerUserId: survey.participant_id,
                partnerWorkflowId: survey.id,
                webhookUrl: this.$api.getDietIdWebhookUrl(),
                email: survey.participant.email,
                firstName: survey.participant.firstname,
                lastName: survey.participant.lastname,
                view: 'nosidebar,noprogressbar,noonboarding,nohelp,nochat,noworkflowresults',
                locale: 'en'
            }

            const response = await Vue.http.post(sso_url,
                JSON.stringify(data),
                {Authorization: `Bearer ${key}`, 'Content-Type': 'application/json'}
            )
            const results = await response.json()

            if (results.success) {
                console.log('SurveyFormDietidMixin:doDietIdLogin  iframe url=', results.url)
                // setting this causes the iframe to load with the DietID UI
                this.dietidIframeUrl = results.url
            } else {
                console.log('SurveyFormDietidMixin:doDietIdLogin  error message=', results.message)
            }
        },

        sendUserInfo() {
            const survey = this.$store.state.survey // loaded by SurveyForm
            console.log('SurveyFormDietidMixin:sendUserInfo  survey=', survey)
            const latestAnthropometrics = survey.participant.latestAnthropometrics
            console.log('SurveyFormDietidMixin:sendUserInfo  latestAnthropometrics=', latestAnthropometrics)

            // NB: don't set any of these payload keys if the values are null,
            // or they will give a validation error
            const payload = {}

            const gender = this.getGender(survey.participant)
            if (gender) {
                payload.gender = gender
            }

            const ageInYears = survey.participant.currentAge
            if (ageInYears) {
                payload.ageInYears = ageInYears
            }

            const weightInPounds = this.getWeight(latestAnthropometrics)
            if (weightInPounds) {
                payload.weightInPounds = weightInPounds
            }

            const heightInInches = this.getHeight(latestAnthropometrics)
            if (heightInInches) {
                payload.heightInInches = heightInInches
            }

            console.log('SurveyFormDietidMixin:sendUserInfo  payload=', payload)
            this.dietidIframeWindow.postMessage(
                JSON.stringify({
                    action: 'setworkflowuserinfo',
                    payload: payload
                }),
                '*'
            )
        },

        isApplicableDietidQuestion(question) {
            if (question.type != 'DIETID') {
                return true
            } else {
                return question.nav_index <= this.dietidMaxQuestions
            }
        },

        isCompletedDietidQuestion(question) {
            const index = question.name.replace('DIETID_', '')
            return +index < this.dietidCompletedQuestionCount
        },

        getLatestAnthropometricsValue(anthropometricName, latestAnthropometrics, optionsLookup = null) {
            if (latestAnthropometrics.length) {
                return latestAnthropometrics[0][anthropometricName]
            } else {
                return null
            }
        },

        getGender(participant) {
            if (participant.gender == 'M') {
                return 'Male'
            } else if (participant.gender == 'F') {
                return 'Female'
            } else {
                return null
            }
        },

        getHeight(latestAnthropometrics) {
            return +this.getLatestAnthropometricsValue('height_in', latestAnthropometrics)
        },

        getWeight(latestAnthropometrics) {
            // NB: option 0 is weight in pounds
            return +this.getLatestAnthropometricsValue('weight_lb', latestAnthropometrics)
        },
    },
    watch: {
        // on survey load, kick off DietID questionnaire
        '$store.state.survey': {
            immediate: true,
            handler: function (newVal, oldVal) {
                const survey = newVal
                if (survey && survey.questionnaire && survey.questionnaire.code == 'DIETID') {
                    this.doDietIdLogin()
                    window.addEventListener('message', this.receiveDietidMessage, false)
                }
            }
        }
    }

}
